// Detail content for each service category. Slugs are the same in both langs.
export const SERVICE_SLUGS = {
    dev: "desarrollo-digital",
    it: "it-infraestructura",
    sec: "ciberseguridad",
    ai: "inteligencia-artificial",
    mkt: "marketing-branding",
    iot: "domotica",
    doc: "gestion-documental",
};

export const slugToKey = (slug) =>
    Object.entries(SERVICE_SLUGS).find(([, s]) => s === slug)?.[0];

export const serviceDetails = {
    es: {
        dev: {
            title: "Desarrollo Digital",
            tagline: "Webs, apps y plataformas e-commerce hechas para escalar contigo.",
            intro:
                "Diseñamos y construimos productos digitales que rinden: rápidos, accesibles y preparados para crecer. Combinamos diseño centrado en el usuario, arquitectura moderna y procesos de entrega continua para que tu negocio se mueva al ritmo del mercado.",
            sections: [
                {
                    title: "Qué hacemos",
                    items: [
                        "Páginas web corporativas y landings de alto rendimiento.",
                        "Aplicaciones a medida (web, móvil y SaaS) con stack moderno.",
                        "Plataformas e-commerce (B2B y B2C) integradas con tu ERP/CRM.",
                        "Sistemas internos, intranets y portales de cliente.",
                        "Modernización y migración de aplicaciones legacy.",
                    ],
                },
                {
                    title: "Cómo lo hacemos",
                    items: [
                        "Discovery con tu equipo: objetivos, casos de uso y métricas de éxito.",
                        "Diseño UX/UI con prototipos navegables antes de escribir código.",
                        "Sprints ágiles, demos quincenales y entregas incrementales.",
                        "QA automatizado, CI/CD y monitorización en producción.",
                    ],
                },
                {
                    title: "Lo que te llevas",
                    items: [
                        "Un producto rápido, mantenible y documentado.",
                        "Equipo que conoce tu negocio y evoluciona contigo.",
                        "Métricas claras de uso, conversión y rendimiento.",
                    ],
                },
            ],
        },
        it: {
            title: "IT & Infraestructura",
            tagline: "La columna vertebral tecnológica de tu negocio, sin sobresaltos.",
            intro:
                "Diseñamos, desplegamos y operamos la infraestructura que sostiene tu día a día: redes, cloud, puesto de trabajo y soporte. Todo monitorizado y con SLAs claros, para que tu equipo solo piense en su negocio.",
            sections: [
                {
                    title: "Qué hacemos",
                    items: [
                        "Diseño y despliegue de redes (LAN, Wi-Fi empresarial, SD-WAN).",
                        "Migración y operación cloud (AWS, Azure, Google Cloud).",
                        "Servidores, virtualización y almacenamiento on-premise o híbrido.",
                        "Renting de equipos: portátiles, sobremesa, móviles y periféricos.",
                        "Mesa de ayuda y soporte técnico con SLA.",
                    ],
                },
                {
                    title: "Cómo lo hacemos",
                    items: [
                        "Auditoría inicial de inventario, riesgos y costes ocultos.",
                        "Plan de migración por fases con cero parada operativa.",
                        "Monitorización 24/7 y reporting mensual de salud de la infraestructura.",
                        "Formación a usuarios finales y procedimientos documentados.",
                    ],
                },
                {
                    title: "Lo que te llevas",
                    items: [
                        "Operación estable, escalable y predecible en coste.",
                        "Visibilidad total sobre tu infraestructura y consumos.",
                        "Un único interlocutor para todo lo IT.",
                    ],
                },
            ],
        },
        sec: {
            title: "Ciberseguridad & Compliance",
            tagline: "Cumplimiento normativo y protección avanzada frente a amenazas reales.",
            intro:
                "Protegemos tu negocio con un enfoque pragmático: alineamos seguridad y normativa (NIS2, ENS, ISO 27001, RGPD) con la realidad operativa de tu empresa. Detectamos, prevenimos y respondemos.",
            sections: [
                {
                    title: "Qué hacemos",
                    items: [
                        "Auditorías de seguridad y análisis de riesgos.",
                        "Adecuación a NIS2, ENS, ISO 27001 y RGPD.",
                        "Hardening de sistemas, EDR/XDR y protección del puesto.",
                        "Gestión de identidades y accesos (IAM, MFA, SSO).",
                        "Plan de respuesta a incidentes y simulacros.",
                    ],
                },
                {
                    title: "Cómo lo hacemos",
                    items: [
                        "Diagnóstico inicial y mapa de cumplimiento priorizado.",
                        "Implementación de controles técnicos y organizativos.",
                        "Documentación y políticas listas para auditoría.",
                        "Monitorización continua y revisiones trimestrales.",
                    ],
                },
                {
                    title: "Lo que te llevas",
                    items: [
                        "Una empresa más resiliente y alineada con la normativa.",
                        "Confianza demostrable hacia clientes y partners.",
                        "Reducción real de la superficie de ataque.",
                    ],
                },
            ],
        },
        ai: {
            title: "Inteligencia Artificial",
            tagline: "Automatización e IA aplicada para escalar procesos y decisiones.",
            intro:
                "La IA no es magia: es ingeniería con criterio. Identificamos los procesos donde la IA aporta valor real, hacemos pilotos rápidos y los llevamos a producción de forma segura y medible.",
            sections: [
                {
                    title: "Qué hacemos",
                    items: [
                        "Automatización de procesos con LLMs (RAG, agentes, asistentes internos).",
                        "Extracción y clasificación documental con IA.",
                        "Modelos predictivos: forecast, scoring, segmentación.",
                        "Integraciones con tus herramientas (ERP, CRM, ticketing).",
                        "Gobierno y privacidad de los datos.",
                    ],
                },
                {
                    title: "Cómo lo hacemos",
                    items: [
                        "Mapeo de procesos y oportunidades con tu equipo.",
                        "Pilotos de 2-4 semanas con métricas claras.",
                        "Despliegue productivo y formación a los usuarios.",
                        "Monitorización de calidad, coste y deriva del modelo.",
                    ],
                },
                {
                    title: "Lo que te llevas",
                    items: [
                        "Procesos más rápidos y con menos error humano.",
                        "Una IA que entiende tu negocio y tus datos.",
                        "Casos de uso priorizados por ROI, no por moda.",
                    ],
                },
            ],
        },
        mkt: {
            title: "Marketing & Branding",
            tagline: "Identidad, contenido y estrategia digital para crecer con foco.",
            intro:
                "Construimos marcas que se reconocen y estrategias que convierten. Unimos diseño, contenido y datos para que cada euro invertido en marketing tenga retorno medible.",
            sections: [
                {
                    title: "Qué hacemos",
                    items: [
                        "Identidad corporativa: branding, naming, manuales.",
                        "Estrategia digital: SEO, SEM, contenido y email.",
                        "Gestión de redes sociales y contenido orgánico.",
                        "Diseño de campañas y producción audiovisual.",
                        "Merchandising corporativo y materiales gráficos.",
                    ],
                },
                {
                    title: "Cómo lo hacemos",
                    items: [
                        "Auditoría de marca y mercado.",
                        "Plan trimestral con KPIs accionables.",
                        "Producción ágil de contenidos y campañas.",
                        "Reporting mensual con foco en negocio, no en vanity metrics.",
                    ],
                },
                {
                    title: "Lo que te llevas",
                    items: [
                        "Una marca coherente en todos los puntos de contacto.",
                        "Embudo de captación predecible y optimizable.",
                        "Equipo creativo + estratégico bajo el mismo techo.",
                    ],
                },
            ],
        },
        iot: {
            title: "Domótica & Espacios Inteligentes",
            tagline: "Hogar y oficina automatizados, con instalación y mantenimiento.",
            intro:
                "Convertimos espacios en entornos inteligentes y eficientes. Iluminación, climatización, control de accesos y eficiencia energética en una sola plataforma, instalada y mantenida por nosotros.",
            sections: [
                {
                    title: "Qué hacemos",
                    items: [
                        "Domótica residencial: iluminación, clima, audio y seguridad.",
                        "Oficinas inteligentes: control de accesos, salas de reunión y aforo.",
                        "Eficiencia energética y monitorización de consumos.",
                        "Integración con KNX, Zigbee, Matter y plataformas cloud.",
                    ],
                },
                {
                    title: "Cómo lo hacemos",
                    items: [
                        "Visita técnica y diseño del proyecto.",
                        "Instalación con equipos propios y materiales certificados.",
                        "Configuración, formación al usuario y entrega.",
                        "Mantenimiento preventivo y correctivo.",
                    ],
                },
                {
                    title: "Lo que te llevas",
                    items: [
                        "Espacios más confortables y eficientes.",
                        "Ahorro real y medible en energía.",
                        "Una sola app para todo el control del entorno.",
                    ],
                },
            ],
        },
        doc: {
            title: "Gestión Documental",
            tagline: "Del papel a la nube, con trazabilidad completa.",
            intro:
                "Digitalizamos, organizamos y custodiamos la información de tu empresa. Procesos más ágiles, búsquedas en segundos y cumplimiento normativo desde el primer día.",
            sections: [
                {
                    title: "Qué hacemos",
                    items: [
                        "Digitalización masiva de archivos físicos.",
                        "Implantación de gestores documentales (DMS) cloud o on-premise.",
                        "Custodia física en archivo seguro.",
                        "Destrucción certificada de documentación sensible.",
                    ],
                },
                {
                    title: "Cómo lo hacemos",
                    items: [
                        "Auditoría del archivo y plan de digitalización por prioridad.",
                        "Captura, indexación y OCR con control de calidad.",
                        "Integración con tus sistemas (ERP, CRM, contabilidad).",
                        "Trazabilidad y registro de accesos.",
                    ],
                },
                {
                    title: "Lo que te llevas",
                    items: [
                        "Información accesible desde cualquier dispositivo.",
                        "Cumplimiento RGPD y políticas de retención claras.",
                        "Menos papel, menos espacio, menos coste.",
                    ],
                },
            ],
        },
    },
    en: {
        dev: {
            title: "Digital Development",
            tagline: "Websites, apps and e-commerce platforms built to scale with you.",
            intro:
                "We design and build digital products that perform: fast, accessible and ready to grow. We combine user-centered design, modern architecture and continuous delivery so your business moves at the pace of the market.",
            sections: [
                { title: "What we do", items: ["Corporate websites and high-performance landing pages.", "Custom apps (web, mobile and SaaS) on a modern stack.", "B2B and B2C e-commerce integrated with your ERP/CRM.", "Internal systems, intranets and customer portals.", "Modernization and migration of legacy applications."] },
                { title: "How we do it", items: ["Discovery with your team: goals, use cases and success metrics.", "UX/UI design with navigable prototypes before writing code.", "Agile sprints, biweekly demos and incremental releases.", "Automated QA, CI/CD and production monitoring."] },
                { title: "What you get", items: ["A fast, maintainable and documented product.", "A team that knows your business and evolves with you.", "Clear usage, conversion and performance metrics."] },
            ],
        },
        it: {
            title: "IT & Infrastructure",
            tagline: "The technological backbone of your business, with no surprises.",
            intro:
                "We design, deploy and operate the infrastructure that supports your day-to-day: networks, cloud, workplace and support. All monitored with clear SLAs so your team can focus on the business.",
            sections: [
                { title: "What we do", items: ["Network design and deployment (LAN, enterprise Wi-Fi, SD-WAN).", "Cloud migration and operation (AWS, Azure, GCP).", "Servers, virtualization and on-prem or hybrid storage.", "Equipment leasing: laptops, desktops, mobiles and peripherals.", "Help desk and tech support with SLA."] },
                { title: "How we do it", items: ["Initial inventory, risk and hidden-cost audit.", "Phased migration plan with zero operational downtime.", "24/7 monitoring and monthly health reporting.", "End-user training and documented procedures."] },
                { title: "What you get", items: ["Stable, scalable and cost-predictable operation.", "Full visibility of your infrastructure and consumption.", "A single point of contact for everything IT."] },
            ],
        },
        sec: {
            title: "Cybersecurity & Compliance",
            tagline: "Regulatory compliance and advanced protection against real threats.",
            intro:
                "We protect your business with a pragmatic approach: aligning security and regulation (NIS2, ENS, ISO 27001, GDPR) with your operational reality. We detect, prevent and respond.",
            sections: [
                { title: "What we do", items: ["Security audits and risk analysis.", "NIS2, ENS, ISO 27001 and GDPR alignment.", "System hardening, EDR/XDR and endpoint protection.", "Identity and access management (IAM, MFA, SSO).", "Incident response plan and drills."] },
                { title: "How we do it", items: ["Initial diagnosis and prioritized compliance map.", "Implementation of technical and organizational controls.", "Documentation and policies ready for audit.", "Continuous monitoring and quarterly reviews."] },
                { title: "What you get", items: ["A more resilient, regulation-aligned company.", "Demonstrable trust toward clients and partners.", "Real reduction of attack surface."] },
            ],
        },
        ai: {
            title: "Artificial Intelligence",
            tagline: "Automation and applied AI to scale processes and decisions.",
            intro:
                "AI isn't magic — it's engineering with judgment. We identify the processes where AI brings real value, run quick pilots and bring them to production safely and measurably.",
            sections: [
                { title: "What we do", items: ["Process automation with LLMs (RAG, agents, internal assistants).", "Document extraction and classification with AI.", "Predictive models: forecast, scoring, segmentation.", "Integrations with your tools (ERP, CRM, ticketing).", "Data governance and privacy."] },
                { title: "How we do it", items: ["Mapping processes and opportunities with your team.", "2-4 week pilots with clear metrics.", "Production rollout and user training.", "Monitoring of quality, cost and model drift."] },
                { title: "What you get", items: ["Faster processes with fewer human errors.", "An AI that understands your business and data.", "Use cases prioritized by ROI, not by hype."] },
            ],
        },
        mkt: {
            title: "Marketing & Branding",
            tagline: "Identity, content and digital strategy to grow with focus.",
            intro:
                "We build brands that get recognized and strategies that convert. We blend design, content and data so every euro spent on marketing has measurable return.",
            sections: [
                { title: "What we do", items: ["Corporate identity: branding, naming, guidelines.", "Digital strategy: SEO, SEM, content and email.", "Social media and organic content management.", "Campaign design and audiovisual production.", "Corporate merchandising and graphic materials."] },
                { title: "How we do it", items: ["Brand and market audit.", "Quarterly plan with actionable KPIs.", "Agile content and campaign production.", "Monthly reporting focused on business, not vanity metrics."] },
                { title: "What you get", items: ["A consistent brand across every touchpoint.", "A predictable, optimizable acquisition funnel.", "Creative and strategic team under the same roof."] },
            ],
        },
        iot: {
            title: "Smart Spaces & IoT",
            tagline: "Automated home and office, with installation and maintenance.",
            intro:
                "We turn spaces into smart, efficient environments. Lighting, climate, access control and energy efficiency on a single platform, installed and maintained by us.",
            sections: [
                { title: "What we do", items: ["Residential automation: lighting, climate, audio and security.", "Smart offices: access control, meeting rooms and occupancy.", "Energy efficiency and consumption monitoring.", "Integration with KNX, Zigbee, Matter and cloud platforms."] },
                { title: "How we do it", items: ["Technical visit and project design.", "Installation with our own team and certified materials.", "Configuration, user training and handover.", "Preventive and corrective maintenance."] },
                { title: "What you get", items: ["More comfortable and efficient spaces.", "Real, measurable energy savings.", "A single app to control your environment."] },
            ],
        },
        doc: {
            title: "Document Management",
            tagline: "From paper to cloud, with full traceability.",
            intro:
                "We digitize, organize and safeguard your company's information. More agile processes, search in seconds and compliance from day one.",
            sections: [
                { title: "What we do", items: ["Mass digitization of physical files.", "DMS implementation, cloud or on-prem.", "Physical custody in secure archive.", "Certified destruction of sensitive documents."] },
                { title: "How we do it", items: ["Archive audit and prioritized digitization plan.", "Capture, indexing and OCR with quality control.", "Integration with your systems (ERP, CRM, accounting).", "Traceability and access log."] },
                { title: "What you get", items: ["Information accessible from any device.", "GDPR compliance and clear retention policies.", "Less paper, less space, less cost."] },
            ],
        },
    },
};

export const ui = {
    es: {
        backHome: "Volver al inicio",
        relatedTitle: "Otros servicios",
        ctaTitle: "¿Quieres explorar este servicio para tu empresa?",
        ctaSubtitle: "Cuéntanos tu reto y te respondemos en menos de 48 horas.",
        ctaButton: "Solicitar asesoría",
        viewService: "Ver servicio",
        allServices: "Todos los servicios",
        servicesEyebrow: "Servicios 360",
        servicesTitle: "Todo lo que tu empresa\nnecesita para crecer.",
        servicesSubtitle:
            "Siete áreas, un único partner. Pulsa cualquier categoría para ver el detalle, casos y entregables concretos.",
    },
    en: {
        backHome: "Back to home",
        relatedTitle: "Other services",
        ctaTitle: "Want to explore this service for your business?",
        ctaSubtitle: "Tell us your challenge and we'll get back to you in under 48 hours.",
        ctaButton: "Request consultation",
        viewService: "View service",
        allServices: "All services",
        servicesEyebrow: "360° Services",
        servicesTitle: "Everything your company\nneeds to grow.",
        servicesSubtitle:
            "Seven areas, one partner. Pick any category for the full breakdown, cases and concrete deliverables.",
    },
};
